import * as types from '../mutation-types'
import { helpers, serverPath } from '../../api/http-common'
import { v4 as uuidv4 } from 'uuid'
import { getLocalState, hasLocalState, SELECTED_OS, SELECTED_BIOC_VERSION } from '../../utils/local-storage.js'
import { packageUtils } from '../../utils/package-utils.js'
import { REPO_TYPE_BIOCONDUCTOR } from '../../constants/repo-types'

export const pageSize = 25

export const mutations = {
  [types.SET_GETTING_PACKAGE_DETAILS] (state, flag) {
    state.gettingPackageDetails = flag
  },
  [types.SET_NO_SUBSCRIPTIONS_FOR_REPO] (state, flag) {
    state.noSubscriptionsForRepo = flag
  },
  [types.SET_GETTING_PACKAGES] (state, incr) {
    state.gettingPackages += incr
  },
  [types.SET_GETTING_PACKAGES_GUID] (state, flag) {
    state.gettingPackagesGuid = flag
  },
  [types.SET_GETTING_SYSTEM_DEPENDENCIES] (state, flag) {
    state.gettingSystemDependencies = flag
  },
  [types.CLEAR_PACKAGES] (state) {
    state.allPackages = []
    state.packagesCount = undefined
    state.currentPage = undefined
    state.selectedPackage = undefined
    state.selectedArchive = undefined
    state.selectedPackageVersion = ''
    state.selectedPackageReleaseDistributions = undefined
  },
  [types.RECEIVE_FIRST_PACKAGES] (state, params) {
    // Check the guid first. This eliminates out-of-order responses
    if (state.gettingPackagesGuid === params.guid) {
      state.allPackages = params.packages
      state.packagesCount = params.packagesCount
      state.currentPage = 1
    }
  },
  [types.RECEIVE_NEXT_PACKAGES] (state, params) {
    state.allPackages = state.allPackages.concat(params.packages)

    // If this call to RECEIVE_NEXT_PACKAGES didn't return any packages,
    // set the packagesCount to the length of allPackages
    if (params.packages && params.packages.length === 0) {
      state.packagesCount = state.allPackages.length
    }

    state.currentPage = params.page
  },
  [types.RECEIVE_PACKAGE_DETAILS] (state, params) {
    if (state.selectedPackage) {
      state.selectedPackage = Object.assign({}, state.selectedPackage, {
        links: (state.selectedPackage.id === params.package.id)
          ? params.package.links
          : state.selectedPackage.links,
        archived: (state.selectedPackage.id === params.package.id)
          ? params.package.archived
          : state.selectedPackage.archived,
        downloads: (state.selectedPackage.id === params.package.id)
          ? params.package.downloads
          : state.selectedPackage.downloads,
        ash: (state.selectedPackage.id === params.package.id)
          ? params.package.ash
          : state.selectedPackage.ash,
        releases: (state.selectedPackage.id === params.package.id)
          ? params.package.releases
          : state.selectedPackage.releases,
        releases_order: (state.selectedPackage.id === params.package.id)
          ? params.package.releases_order
          : state.selectedPackage.releases_order,
      })
    }
  },
  [types.RECEIVE_PACKAGE_VERSIONS] (state, params) {
    state.selectedPackage = Object.assign({}, state.selectedPackage, {
      archived: (state.selectedPackage.name === params.name)
        ? params.versions
        : state.selectedPackage.archived,
    })
  },
  [types.RECEIVE_PACKAGE_README] (state, params) {
    if (state.selectedPackage.id === params.packageId) {
      state.selectedPackage = Object.assign({}, state.selectedPackage, {
        readmeFile: params.readmeFile,
      })
    }
  },
  [types.RECEIVE_PACKAGE_SYSTEM_DEPENDENCIES] (state, params) {
    if (state.selectedPackage.id === params.packageId) {
      state.selectedPackage = Object.assign({}, state.selectedPackage, {
        systemInstallInstructions: params.instructions,
      })
    }
  },
  [types.SET_CURRENT_REPO] (state) {
    state.allPackages = []
    state.packagesCount = undefined
    state.currentPage = undefined
    state.selectedPackage = undefined
    state.selectedArchive = undefined
    state.searchString = ''
    state.noSubscriptionsForRepo = false
  },
  [types.SET_SELECTED_PACKAGE] (state, packageItem) {
    state.selectedPackage = packageItem
    state.selectedArchive = undefined
  },
  [types.SET_SELECTED_ARCHIVE] (state, archiveItem) {
    state.selectedArchive = archiveItem
  },
  [types.SET_SELECTED_PACKAGE_VERSION] (state, versionItem) {
    state.selectedPackageVersion = versionItem
  },
  [types.SET_SELECTED_PACKAGE_RELEASE_DISTRIBUTIONS] (state, distributions) {
    state.selectedPackageReleaseDistributions = distributions
  },
  [types.SET_SELECTED_OS] (state, os) {
    state.selectedOS = os
  },
  [types.SET_SELECTED_BIOC_VERSION] (state, biocVer) {
    state.selectedBiocVersion = biocVer
  },
  [types.VERIFY_SELECTED_OS] (state, distros) {
    if (state.selectedOS.distribution === '') {
      state.selectedOS = distros[0]
    } else {
      if (!distros.some(
        distro =>
          distro.distribution === state.selectedOS.distribution &&
          distro.release === state.selectedOS.release &&
          distro.display === state.selectedOS.display &&
          distro.binaryURL === state.selectedOS.binaryURL &&
          distro.binaryDisplay === state.selectedOS.binaryDisplay &&
          distro.sysReqs === state.selectedOS.sysReqs,
      )) {
        state.selectedOS = distros[0]
      }
    }
  },
  [types.VERIFY_SELECTED_BIOC_VERSION] (state, biocVersions) {
    if (biocVersions && biocVersions.length > 0) {
      if (state.selectedBiocVersion.bioc_version === '') {
        state.selectedBiocVersion = biocVersions[0]
      } else {
        if (!biocVersions.some(
          ver =>
            ver.r_version === state.selectedBiocVersion.r_version &&
            ver.bioc_version === state.selectedBiocVersion.bioc_version &&
            ver.cran_snapshot === state.selectedBiocVersion.cran_snapshot,
        )) {
          state.selectedBiocVersion = biocVersions[0]
        }
      }
    }
  },
}

const module = HTTP => ({
  state: {
    gettingPackages: 0,
    gettingPackagesGuid: '',
    gettingPackageDetails: false,
    allPackages: [],
    noSubscriptionsForRepo: false,
    packagesCount: undefined,
    currentPage: undefined,
    selectedPackage: undefined,
    selectedArchive: undefined,
    selectedPackageVersion: '',
    selectedPackageReleaseDistributions: undefined,
    selectedOS: getLocalState(SELECTED_OS) ? getLocalState(SELECTED_OS) : {
      distribution: '',
      release: '',
      display: '',
      binaryURL: '',
      binaryDisplay: '',
      sysReqs: '',
    },
    selectedBiocVersion: hasLocalState(SELECTED_BIOC_VERSION) ? getLocalState(SELECTED_BIOC_VERSION) : {
      r_version: '',
      bioc_version: '',
    },
    gettingSystemDependencies: false,
  },
  mutations,
  actions: {
    clearSelectedPackage ({ commit }) {
      commit(types.SET_SELECTED_PACKAGE, null)
    },
    async getFirstPackages ({ commit, state, rootState }, params) {
      try {
        const guid = uuidv4()
        commit(types.SET_GETTING_PACKAGES, 1)
        commit(types.CLEAR_PACKAGES)
        commit(types.SET_GETTING_PACKAGES_GUID, guid)

        let response

        const versionQuery = rootState.repos.currentRepoType === REPO_TYPE_BIOCONDUCTOR ? `&bioc_version=${state.selectedBiocVersion.bioc_version}` : ''

        if (params.packages) {
          response = await packageUtils.fetchPackages(HTTP, commit, `/repos/${params.repoId}/packages?names=${params.packages.join(',')}${versionQuery}`)
          if (!response) {
            return
          }
        } else if (params.searchString && params.searchString.length < 3) {
          // Ignore 404s when doing exact search matching
          try {
            response = await HTTP.get(`/repos/${params.repoId}/packages?name=${params.searchString}&case_insensitive=true${versionQuery}`)
          } catch {
            commit(types.SET_GETTING_PACKAGES, -1)
            return
          }
        } else {
          if (params.packageName) {
            params.searchString = params.packageName
          }

          response = await packageUtils.fetchPackages(HTTP, commit, `/repos/${params.repoId}/packages?_sort=temp_sort_name&_order=asc&_page=1&_limit=${pageSize}&name_like=${params.searchString}&exact_first=true${versionQuery}`)
          if (!response) {
            return
          }
        }

        const packages = response.data
        const count = response.headers['x-total-count'] || 1

        if (rootState.repos.currentRepoId === params.repoId) {
          commit(types.RECEIVE_FIRST_PACKAGES, { packages: packages, packagesCount: +count, guid: guid })
          if (packages.length) {
            commit(types.SET_SELECTED_PACKAGE, packages[0])
          }
        }
        commit(types.SET_GETTING_PACKAGES, -1)
      } catch (e) {
        commit(types.SET_GETTING_PACKAGES, -1)
        console.log(e)
      }
    },
    async getNextPackages ({ commit, state, rootState }, params) {
      try {
        commit(types.SET_GETTING_PACKAGES, 1)

        const versionQuery = rootState.repos.currentRepoType === REPO_TYPE_BIOCONDUCTOR ? `&bioc_version=${state.selectedBiocVersion.bioc_version}` : ''

        let page = state.currentPage
        let packages
        if (params.packages) {
          const response = await HTTP.get(`/repos/${params.repoId}/packages?names=${params.packages.join(',')}${versionQuery}`)
          packages = response.data
        } else {
          page = state.currentPage + 1
          const response = await HTTP.get(`/repos/${params.repoId}/packages?_sort=temp_sort_name&_order=asc&_page=${page}&_limit=${pageSize}&name_like=${params.searchString}&exact_first=true${versionQuery}`)
          packages = response.data
        }

        if (rootState.repos.currentRepoId === params.repoId) {
          commit(types.RECEIVE_NEXT_PACKAGES, { packages: packages, page: page })
        }
        commit(types.SET_GETTING_PACKAGES, -1)
      } catch (e) {
        commit(types.SET_GETTING_PACKAGES, -1)
        console.log(e)
      }
    },
    async getPackage ({ commit, state, rootState }, params) {
      try {
        const versionQuery = rootState.repos.currentRepoType === REPO_TYPE_BIOCONDUCTOR ? `&bioc_version=${state.selectedBiocVersion.bioc_version}` : ''
        commit(types.SET_GETTING_PACKAGE_DETAILS, true)
        const response = await HTTP.get(`/repos/${params.repoId}/packages/${params.name}?id=${params.id}${versionQuery}`)
        const pkg = response.data
        commit(types.RECEIVE_PACKAGE_DETAILS, { package: pkg })
        commit(types.SET_GETTING_PACKAGE_DETAILS, false)
      } catch (e) {
        commit(types.SET_GETTING_PACKAGE_DETAILS, false)
        console.log(e)
      }
    },
    async getPackageVersions ({ commit }, params) {
      try {
        commit(types.SET_GETTING_PACKAGE_DETAILS, true)
        const response = await HTTP.get(`/sources/${params.sourceId}/packages/${params.name}/versions`)
        const versions = response.data
        commit(types.RECEIVE_PACKAGE_VERSIONS, { versions, name: params.name })
        commit(types.SET_GETTING_PACKAGE_DETAILS, false)
      } catch (e) {
        commit(types.SET_GETTING_PACKAGE_DETAILS, false)
        console.log(e)
      }
    },
    async getPackageReadme ({ commit }, params) {
      try {
        // Are we running in production?
        const { isApiProd } = helpers.environments(process.env.API_ENV, process.env.NODE_ENV)
        const url = isApiProd ? `/packages/${params.packageId}/readme` : params.readmeFileName
        commit(types.SET_GETTING_PACKAGE_DETAILS, true)
        const response = await HTTP.get(url)
        const readmeFile = response.data
        commit(types.RECEIVE_PACKAGE_README, { packageId: params.packageId, readmeFile: readmeFile })
        commit(types.SET_GETTING_PACKAGE_DETAILS, false)
      } catch (e) {
        commit(types.SET_GETTING_PACKAGE_DETAILS, false)
        console.log(e)
      }
    },
    async getPackageSystemDependencies ({ commit, state, rootState }, params) {
      try {
        const versionQuery = rootState.repos.currentRepoType === REPO_TYPE_BIOCONDUCTOR ? `&bioc_version=${state.selectedBiocVersion.bioc_version}` : ''
        commit(types.SET_GETTING_SYSTEM_DEPENDENCIES, true)
        const { repoId, pkg, os } = params
        const response = await HTTP.get(`/repos/${repoId}/packages/${pkg.name}/sysreqs?id=${pkg.id}&distribution=${os.distribution}&release=${os.release}${versionQuery}`)

        // Transforms a pre/post install command to what we need for the UI
        const transformExtraCommand = item => {
          return item.command
            // Commands stay as-is
            ? item.command
            // Scripts are transformed to include a one-liner to download and run the command
            : `bash -c "$(curl -L ${serverPath()}__api__/sysreqs/script/${item.script})"`
        }

        // Transforms a package (which may include install scripts and pre/post install commands)
        // to a single array of commands, ordered correctly as pre_install, install, post_install.
        const packageSet = (result, data) => {
          const all = [].concat(
            (data.pre_install || []).map(transformExtraCommand),
            data.install_scripts || [],
            (data.post_install || []).map(transformExtraCommand),
          )
          if (all.length) {
            result = result.concat(all)
          }
          return result
        }

        // Concatenate all the instructions together, with dependencies coming first. Reduce to
        // the set of instructions provided for each dependency + the main package.
        const allInstructions = [].concat(response.data.dependencies || [], response.data).reduce(packageSet, [])

        // Remove duplicate scripts
        const instructions = [...new Set(allInstructions)]

        commit(types.RECEIVE_PACKAGE_SYSTEM_DEPENDENCIES, { packageId: pkg.id, instructions: instructions })
        commit(types.SET_GETTING_SYSTEM_DEPENDENCIES, false)
      } catch (e) {
        commit(types.SET_GETTING_SYSTEM_DEPENDENCIES, false)
        console.log(e)
      }
    },
  },
  getters: {},
})

export default {
  module,
}
